import React, {FC, useState} from "react";
import {Typography, Button, lighten, Tooltip} from "@mui/material";
import {useDispatch, useSelector} from "../../store";
import Grid from "@mui/material/Grid";
import Hidden from "@mui/material/Hidden";
import Box from "@mui/material/Box";
import {StyledRating} from "../styled-rating";
import {Review} from "./review";
import CreateIcon from "@mui/icons-material/Create";
import {useTranslation} from "next-i18next";
import {styled} from "@mui/material/styles";
import {useAuth} from "../../hooks/use-auth";
import {setShowAuthWall} from "../../slices/user-session-slice";
import {scoreProps} from "seacrush-core";
import {ReviewUpsertDialog} from "./review-upsert-dialog";
import {openNewUpsertDialog} from "../../slices/reviews-slice";
import LinearProgress, { linearProgressClasses, LinearProgressProps } from '@mui/material/LinearProgress';

const round = (number: number) => {
    return number.toFixed(1);
};

const BorderLinearProgress = styled(LinearProgress)<LinearProgressProps>(({theme}) => ({
    marginTop: 1,
    height: 8,
    backgroundColor: lighten("#47434a", 0.5),
    [`& .${linearProgressClasses.bar}`]: {
        borderRadius: 0,
        backgroundColor: "#e87474",
    },
}))


export const ReviewList: FC = () => {

    const [showAllReviews, setShowAllReviews] = useState(false);

    const dispatch = useDispatch()

    const {reviews, aggregateRating, overallScores, isSubmitting} = useSelector((state) => {
        const {selectedAggregateRating, selectedOverallScores, selectedTripReviews, isSubmitting} = state.reviews
        return {
            reviews: selectedTripReviews,
            aggregateRating: selectedAggregateRating ?? {
                ratingValue: 0,
                reviewCount: 0
            },
            overallScores: selectedOverallScores,
            isSubmitting
        }
    });

    const {isAuthenticated, isInitialized} = useAuth()


    const {t, i18n} = useTranslation();


    const handleLoadReviewsClicked = (e: any) => {
        if (isAuthenticated) {
            setShowAllReviews(true);
        } else if (isInitialized) {
            dispatch(setShowAuthWall(true));
        }
    };

    const reviewButton = (

        <div>
            <Tooltip placement={"left"} title={isAuthenticated ? null : "Sign in to leave a review!"} arrow>
                <Button
                    // disabled={!isAuthenticated}
                    variant={"contained"}
                    size={"medium"}
                    color={"primary"}
                    // className={classes.reviewButton}
                    onClick={() => {
                        if (!isAuthenticated) {
                            dispatch(setShowAuthWall(true))
                        } else {
                            dispatch(openNewUpsertDialog(i18n.language))
                        }
                    }}
                >
                    <CreateIcon fontSize={"small"} style={{marginRight: 4}}/>
                    {t("write-a-review")}
                </Button>
            </Tooltip>
        </div>

    );

    return <>

        <Grid
            container
            spacing={0}
            direction={"row"}
            justifyContent={"flex-end"}
            alignItems={"flex-end"}
            sx={{mt: 2}}
        >
            <Grid item xs={12} md={7}>
                {reviews.length > 0 && (
                    <Typography variant={"h3"}>{t("overall-rating")}</Typography>
                )}
            </Grid>
            <Grid item xs={12} md={5}>
                <Hidden smDown>
                    <Box sx={{textAlign: "right"}}>{reviewButton}</Box>
                </Hidden>
            </Grid>
        </Grid>
        <Grid
            container
            spacing={0}
            direction={"row"}
            justifyContent={"flex-end"}
            sx={{my: 2}}
        >
            <Grid item xs={12} md={5}>
                {reviews.length > 0 && (
                    <Box pl={2} pb={3}>
                        <StyledRating
                            name='overall-score'
                            value={aggregateRating?.ratingValue / 2}
                            size={"large"}
                            readOnly
                            max={5}
                            precision={0.1}
                        />
                        <Typography component={"div"}
                                    style={{
                                        backgroundColor: "#384e7b",
                                        color: "white",
                                        display: "inline-block",
                                        fontSize: "1.5rem",
                                        fontWeight: "bold",
                                        textAlign: "center",
                                        minWidth: "3rem",
                                        padding: 8,
                                        borderRadius: 4,
                                        marginLeft: 4,
                                        verticalAlign: "middle",
                                    }}
                        >
                            {aggregateRating?.ratingValue || "?"}
                        </Typography>
                        <br/>
                    </Box>
                )}
            </Grid>
            <Grid item xs={12} md={7}>
                <Box sx={{
                    pr: {
                        xs: 0,
                        md: 0
                    }
                }}>
                    {reviews.length > 0 && (
                        <Grid
                            container
                            spacing={1}
                            direction={"row"}
                            justifyContent={"flex-end"}
                        >
                            {overallScores && (
                                <React.Fragment>
                                    <Grid
                                        item
                                        xs={5}
                                        sm={5}
                                        style={{textAlign: "right", overflow: "hidden"}}
                                    >
                                        {scoreProps.map((name, idx) => (
                                            <div key={idx} style={{height: 26}}>
                                                {t(`score-${name}`)}
                                            </div>
                                        ))}
                                    </Grid>

                                    <Grid item style={{paddingTop: 16, flexGrow: 1}}>
                                        {scoreProps.map((name, idx) => (
                                            <div key={idx} style={{height: 26}}>
                                                <BorderLinearProgress
                                                    variant='determinate'
                                                    color={"secondary"}
                                                    aria-label={`${name} score`}
                                                    value={overallScores[name].avg * 10}
                                                />
                                            </div>
                                        ))}
                                    </Grid>
                                    <Grid item style={{textAlign: "right"}}>
                                        {scoreProps.map((name, idx) => (
                                            <Typography
                                                key={idx}
                                                component={"div"}
                                                style={{
                                                    height: 26,
                                                    color: "rgb(102,102,102)",
                                                    lineHeight: "unset",
                                                }}
                                            >
                                                <strong>
                                                    {round(overallScores[name].avg) || "?"}
                                                </strong>
                                                /10
                                            </Typography>
                                        ))}
                                    </Grid>
                                </React.Fragment>
                            )}
                        </Grid>
                    )}
                </Box>
            </Grid>
        </Grid>

        <Hidden mdUp>
            <Box sx={{textAlign: "center"}} mb={3}>
                {reviewButton}
            </Box>
        </Hidden>


        <ReviewUpsertDialog/>

        <Box
            sx={{
                pl: 1,
                ...(!showAllReviews && {
                    position: "relative",
                    maxHeight: 120,
                    overflow: "hidden",
                    "&::before": {
                        background: "linear-gradient(0deg, white, transparent)",
                        content: "'\\00a0'",
                        height: "100%",
                        position: "absolute",
                        width: "100%",
                        zIndex: 4,
                    },
                }),

            }}
        >
            {reviews.map((review, idx) => (
                <div
                    key={idx}
                    style={{borderTop: idx === 0 ? "none" : "2px solid #e7e7e7"}}
                >
                    <Review review={review}/>
                </div>
            ))}
        </Box>

        {(reviews.length > 0 || (!isAuthenticated)) &&
            !showAllReviews && (
                <div style={{textAlign: "center"}}>
                    <Button
                        sx={{mb: 2}}
                        color={"primary"}
                        variant={"outlined"}
                        onClick={handleLoadReviewsClicked}
                    >
                        {t("read-reviews")}
                    </Button>
                </div>
            )}
    </>

}
